import React, { useState, memo, useMemo, useEffect } from 'react';
import { showWarning } from '../utils/notifications';
import ModalDialog from './ModalDialog';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useFirebaseListener } from '../hooks/useFirebaseListener';
import { useOptimizedCallback } from '../hooks/useOptimizedCallback';

const VendorCard = memo(({ vendor, onRecharge, onToggleStatus, onEdit, isAdmin = false, isSupervisor = false, lowBalance = false, availableTickets = {}, canRecharge = true, isPlusMode = false, availableTokens = [] }) => {
  // State optimizado con useMemo para inicialización
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState('moto');

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('efectivo');
  const [isRecharging, setIsRecharging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Estados para modo Plus (cantidad y token manuales)
  const [manualTicketAmount, setManualTicketAmount] = useState('');
  const [selectedManualToken, setSelectedManualToken] = useState('');
  
  // Memoizar datos de edición para evitar recreaciones innecesarias
  const [editData, setEditData] = useState(() => ({
    nombre: vendor.name,
    cedula: vendor.identificationNumber,
    telefono: vendor.telefono,
    zona: vendor.zone
  }));
  
  // Estado para tickets en tiempo real (Firebase + actualización optimista desde API)
  const [currentTickets, setCurrentTickets] = useState(() => ({
    moto: vendor.ticketsMoto || 0,
    auto: vendor.ticketsAuto || 0
  }));

  // Sincronizar cuando el padre actualiza vendor (ej. tras recarga exitosa con data.data)
  useEffect(() => {
    const moto = vendor.ticketsMoto ?? 0;
    const auto = vendor.ticketsAuto ?? 0;
    setCurrentTickets(prev => (prev.moto !== moto || prev.auto !== auto) ? { moto, auto } : prev);
  }, [vendor.ticketsMoto, vendor.ticketsAuto]);
  
  const [isTicketsLow, setIsTicketsLow] = useState(lowBalance);
  const [showDetails, setShowDetails] = useState(false);
  
  // Modales con estado inicial memoizado
  const [confirmModal, setConfirmModal] = useState(() => ({
    isOpen: false, 
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'confirm'
  }));
    const [alertModal, setAlertModal] = useState(() => ({
    isOpen: false,
    title: '',
    message: '',
    type: 'alert'
  }));  // Hook personalizado para el listener de Firebase con cleanup automático
  useFirebaseListener(
    () => {
      if (!vendor.userId) return () => {};
      
      const docRef = doc(db, 'usuarios', vendor.userId);
      return onSnapshot(docRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const newTicketsMoto = data.ticketsMoto || 0;
          const newTicketsAuto = data.ticketsAuto || 0;
          
          const hasActualChange = (newTicketsMoto !== currentTickets.moto) || (newTicketsAuto !== currentTickets.auto);
          if (hasActualChange && process.env.NODE_ENV === 'development') {
            console.log(`📊 Cambio tickets: ${vendor.name} - Motos: ${currentTickets.moto}→${newTicketsMoto}, Autos: ${currentTickets.auto}→${newTicketsAuto}`);
          }
          
          setCurrentTickets({
            moto: newTicketsMoto,
            auto: newTicketsAuto
          });
          
          const isLow = newTicketsAuto < 10; // Solo alertar por autos bajos
          setIsTicketsLow(isLow);
          
          // 🚨 ALERTA URGENTE: Mostrar alerta automática cuando los tickets de AUTOS están bajos
          // ALERTA DESACTIVADA POR SOLICITUD DEL CLIENTE
          // if (isLow && !isRecharging) {
          //   const lowTypes = [];
          //   if (newTicketsAuto < 10) lowTypes.push(`🚗 Autos: ${newTicketsAuto}`);
          //   
          //   setTimeout(() => {
          //     setAlertModal({
          //       isOpen: true,
          //       title: '🚨 RECARGA URGENTE NECESARIA - AUTOS',
          //       message: `⚠️ El CA ${vendor.name} tiene tickets críticos de autos:\n\n${lowTypes.join('\n')}\n📍 Zona: ${vendor.zone || 'Sin zona asignada'}\n🆔 ID: ${vendor.numberCa}\n\n🔴 ATENCIÓN: Con menos de 10 tickets de autos, el CA no puede operar normalmente.\n\n💡 Se recomienda recargar inmediatamente para evitar interrupciones en el servicio.`,
          //       type: 'urgent'
          //     });
          //   }, 1000); // Retraso de 1 segundo para que no interfiera con otras operaciones
          // }
        }
      });
    },
    [vendor.userId, vendor.name, isRecharging]
  );

  // Balance del sistema por tipo de vehículo
  const sistemaDisponibleMoto = Number(availableTickets.cantidadMoto) || 0;
  const sistemaDisponibleAuto = Number(availableTickets.cantidadAuto) || 0;
  // hasActiveToken: false solo si explícitamente recibimos false (evitar falso negativo al inicio)
  const hasActiveToken = availableTickets.hasActiveToken !== false;
  const sistemaEnContraParaTipo = selectedVehicleType === 'moto'
    ? sistemaDisponibleMoto <= 0
    : sistemaDisponibleAuto <= 0;
  const sinToken = !hasActiveToken;
  const ambosEnContra = (sistemaDisponibleMoto <= 0 && sistemaDisponibleAuto <= 0) || sinToken;
  const canRechargeEffective = canRecharge && !sistemaEnContraParaTipo && !sinToken;

  // Memoizar paquetes de tickets para evitar recalculaciones
  const ticketPackages = useMemo(() => ({
    moto: [
      { price: 5.50, tickets: 40, label: '40 tickets - $5.50' },
      { price: 11.00, tickets: 80, label: '80 tickets - $11.00' },
      { price: 22.00, tickets: 160, label: '160 tickets - $22.00' }
    ],
    auto: [
      { price: 5.50, tickets: 25, label: '25 tickets - $5.50' },
      { price: 11.00, tickets: 50, label: '50 tickets - $11.00' },
      { price: 22.00, tickets: 100, label: '100 tickets - $22.00' }
    ]
  }), []);

  const paymentMethodLabelMap = {
    efectivo: '💵 EFECTIVO',
    transferencia: '🏦 TRANSFER.',
    deposito: '🏛️ DEPÓSITO'
  };

  const getPaymentMethodLabel = (method) => paymentMethodLabelMap[method] || method.toUpperCase();
  // Handlers optimizados con useCallback
  const handleEditSubmit = useOptimizedCallback(async (e) => {
    e.preventDefault();
    try {
      const result = await onEdit(vendor.userId, editData);
      setIsEditing(false);
      
      if (result && result.success) {
        setAlertModal({
          isOpen: true,
          title: 'Datos actualizados',
          message: `La información del CA ${result.userName} ha sido actualizada correctamente.`,
          type: 'success'
        });
      }
    } catch (error) {
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: `Error al editar: ${error.message}`,
        type: 'error'
      });
    }
  }, [onEdit, vendor.userId, editData], 300);

  const handleToggleStatus = useOptimizedCallback(async () => {
    const action = vendor.status === 'active' ? 'desactivar' : 'activar';
    
    setConfirmModal({
      isOpen: true,
      title: `Confirmar ${action}`,
      message: `¿Está seguro de ${action} al usuario ${vendor.name}?`,
      type: 'confirm',
      onConfirm: async () => {
        setConfirmModal(prev => ({...prev, isOpen: false}));
        
        try {
          const result = await onToggleStatus(vendor.userId, vendor.status);
          
          if (result && result.success) {
            const statusText = result.newStatus === 'active' ? 'activado' : 'desactivado';
            setAlertModal({
              isOpen: true,
              title: 'Estado actualizado',
              message: `El CA ${vendor.name} ha sido ${statusText} correctamente.`,
              type: 'success'
            });
          }
        } catch (error) {
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: `Error al cambiar estado: ${error.message}`,
            type: 'error'
          });
        }
      }
    });
  }, [onToggleStatus, vendor.userId, vendor.status, vendor.name], 500);  
  const handleRechargeSubmit = useOptimizedCallback(async (e) => {
    e.preventDefault();
    
    if (!canRecharge) {
      showWarning("Recargas deshabilitadas", "El sistema no tiene suficientes tickets disponibles para procesar recargas");
      return;
    }
    
    // Modo Plus: validar cantidad manual (token lo selecciona api-recarga-tickets desde ticketRequestsInternas)
    if (isPlusMode) {
      if (!manualTicketAmount || parseInt(manualTicketAmount) <= 0) {
        showWarning("Cantidad inválida", "Ingrese una cantidad válida de tickets");
        return;
      }
      
      const ticketAmount = parseInt(manualTicketAmount);
      // Calcular precio aproximado (11.00 por 50 tickets para autos, 11.00 por 80 tickets para motos)
      const pricePerTicket = selectedVehicleType === 'auto' ? 11.00 / 50 : 11.00 / 80;
      const calculatedPrice = ticketAmount * pricePerTicket;
      const tokenInfo = selectedManualToken ? `\nToken (referencia): ${selectedManualToken}` : '\nToken: selección automática (api-recarga-tickets)';
      
      setConfirmModal({
        isOpen: true,
        title: 'Confirmar recarga de tickets (Recargas Plus)',
        message: `¿Está seguro de recargar al CA ${vendor.name}?\n\nTipo de vehículo: ${selectedVehicleType.toUpperCase()}\nCantidad: ${ticketAmount} tickets${tokenInfo}\nPrecio aproximado: $${calculatedPrice.toFixed(2)}\nMétodo de pago: ${getPaymentMethodLabel(selectedPaymentMethod)}`,
        type: 'confirm',
        onConfirm: async () => {
          setConfirmModal(prev => ({...prev, isOpen: false}));
          
          try {
            setIsRecharging(true);
            
            await onRecharge(vendor.userId, {
              vehicleType: selectedVehicleType,
              tickets: ticketAmount,
              price: calculatedPrice,
              paymentMethod: selectedPaymentMethod
            });
            
            setManualTicketAmount('');
            setSelectedManualToken('');
            setSelectedPaymentMethod('efectivo');
            setIsRecharging(false);
            
            setAlertModal({
              isOpen: true,
              title: '¡Recarga de Tickets Exitosa! (Recargas Plus)',
              message: `Se ha recargado al CA ${vendor.name}.\n\nTipo: ${selectedVehicleType.toUpperCase()}\nTickets agregados: ${ticketAmount}\nPrecio: $${calculatedPrice.toFixed(2)}\nMétodo de pago: ${getPaymentMethodLabel(selectedPaymentMethod)}\n\n✅ Los tickets se actualizarán automáticamente.`,
              type: 'alert'
            });
          } catch (error) {
            console.error(`❌ API falló:`, error.message);
            setIsRecharging(false);
            setAlertModal({
              isOpen: true,
              title: '🚫 RECARGA BLOQUEADA',
              message: `❌ NO SE PUDO PROCESAR LA RECARGA\n\n${error.message}\n\n⚠️ Los tickets NO han sido actualizados por seguridad.`,
              type: 'error'
            });
          }
        }
      });
      return;
    }
    
    // Modo normal: validar paquete seleccionado
    if (!selectedPackage) {
      showWarning("Paquete no seleccionado", "Seleccione un paquete de tickets");
      return;
    }

    const packageInfo = ticketPackages[selectedVehicleType].find(pkg => 
      pkg.price === selectedPackage.price && pkg.tickets === selectedPackage.tickets
    );

    if (!packageInfo) {
      showWarning("Paquete inválido", "El paquete seleccionado no es válido");
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Confirmar recarga de tickets',
      message: `¿Está seguro de recargar al CA ${vendor.name}?\n\nTipo de vehículo: ${selectedVehicleType.toUpperCase()}\nPaquete: ${packageInfo.tickets} tickets\nPrecio: $${packageInfo.price.toFixed(2)}\nMétodo de pago: ${getPaymentMethodLabel(selectedPaymentMethod)}`,
      type: 'confirm',
      onConfirm: async () => {        setConfirmModal(prev => ({...prev, isOpen: false}));
        
        try {
          setIsRecharging(true);
          
          // ✅ SOLO ACTUALIZAR TICKETS SI LA VALIDACIÓN API ES EXITOSA
          await onRecharge(vendor.userId, {
            vehicleType: selectedVehicleType,
            tickets: packageInfo.tickets,
            price: packageInfo.price,
            paymentMethod: selectedPaymentMethod
          });
          
          // ✅ Si llegamos aquí, la API validó correctamente
          console.log(`✅ API validada exitosamente - Firebase será actualizado por VendorsList.js`);
          console.log(`🔄 Los tickets se actualizarán automáticamente vía listener de Firebase`);
          
          setSelectedPackage(null);
          setSelectedPaymentMethod('efectivo');
          setIsRecharging(false);
          
          setAlertModal({
            isOpen: true,
            title: '¡Recarga de Tickets Exitosa!',
            message: `Se ha recargado el paquete al CA ${vendor.name}.\n\nTipo: ${selectedVehicleType.toUpperCase()}\nTickets agregados: ${packageInfo.tickets}\nPrecio: $${packageInfo.price.toFixed(2)}\nMétodo de pago: ${getPaymentMethodLabel(selectedPaymentMethod)}\n\n✅ Los tickets se actualizarán automáticamente en unos segundos.`,
            type: 'alert'
          });
        } catch (error) {
          // ❌ API FALLÓ - NO SE ACTUALIZAN TICKETS
          console.error(`❌ API falló - NO se actualizarán tickets locales:`, error.message);
          console.error(`🔍 DEBUG: Excepción capturada en VendorCard.js`);
          console.error(`🔍 DEBUG: Tipo de error:`, error.constructor.name);
          console.error(`🔍 DEBUG: Stack trace:`, error.stack);
          console.error(`🚨 CRÍTICO: Si ves el mensaje 'Tickets update for' después de esto, hay un fallo de seguridad`);
          
          setIsRecharging(false);
          setAlertModal({
            isOpen: true,
            title: '🚫 RECARGA BLOQUEADA',
            message: `❌ NO SE PUDO PROCESAR LA RECARGA\n\n${error.message}\n\n⚠️ Los tickets NO han sido actualizados por seguridad.`,
            type: 'error'
          });
          
          console.error(`🔍 DEBUG: Modal de error configurado - proceso terminado en VendorCard`);
        }
      }
    });  }, [selectedPackage, ticketPackages, selectedVehicleType, vendor.name, vendor.userId, onRecharge, currentTickets, selectedPaymentMethod, isPlusMode, manualTicketAmount], 500);

  return (
    <>
      <div className="bg-white rounded-lg shadow-md">
        {/* Admin badge at the top of the card */}
        {isAdmin && (
          <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded absolute right-2 top-2">
       
          </div>
        )}
        
        <div className="p-4 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{vendor.name}</h3>
              <p className="text-sm text-gray-500">ID: {vendor.numberCa}</p>
              <p className="text-xs text-blue-600 font-medium">📍 {vendor.zone || 'Sin zona asignada'}</p>
            </div>
            {/* Mostrar estado y botón de toggle solo si no están deshabilitadas las acciones */}
            {!isAdmin && (
               <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                vendor.status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {vendor.status === 'active' ? 'Activo' : 'Inactivo'}
              </span>
             )}
          </div>
        </div>          <div className="p-4">
          {/* Botón para mostrar/ocultar detalles adicionales - Solo visible cuando NO está en modo recarga */}
          {!isRecharging && (
            <div className="mb-3">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
              >
                {showDetails ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    Ocultar Detalles
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    Mostrar Detalles del CA
                  </>
                )}
              </button>
            </div>
          )}

          {/* Mostrar información detallada solo cuando está activada Y NO está en modo recarga */}
          {!isRecharging && showDetails && (
            <>
              <div className="flex justify-between mb-3">
                <div>
                  <p className="text-sm text-gray-500">Cédula</p>
                  <p className="font-medium">{vendor.identificationNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <p className="font-medium">{vendor.telefono}</p>
                </div>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-gray-500">Zona</p>
                <p className="font-medium">{vendor.zone}</p>
              </div>
              
              <div className="flex justify-between mb-3">
                <div>
                  <p className="text-sm text-gray-500">Tickets Vendidos</p>
                  <p className="font-medium">{vendor.ticketsSold || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ingresos</p>
                  <p className="font-medium">${vendor.revenueGenerated || 0}</p>
                </div>
              </div>
            </>
          )}
          
          {/* Información siempre visible: tickets disponibles */}
          <div className="mb-3">
            <p className="text-sm text-gray-500 mb-2">Tickets Disponibles</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-blue-50 p-2 rounded">
                <p className="text-xs text-gray-600">Motos</p>
                <p className={`font-bold ${currentTickets.moto < 10 ? 'text-red-600' : 'text-blue-600'}`}>
                  {currentTickets.moto}
                </p>
              </div>
              <div className="bg-green-50 p-2 rounded">
                <p className="text-xs text-gray-600">Autos</p>
                <p className={`font-bold ${currentTickets.auto < 10 ? 'text-red-600' : 'text-green-600'}`}>
                  {currentTickets.auto}
                </p>
              </div>
            </div>
          </div>
            <div className="border-t pt-3 mt-3">
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm text-gray-500">Estado de Tickets</p>
              <p className={`font-medium text-sm ${isTicketsLow ? 'text-red-600' : 'text-green-600'}`}>
                {isTicketsLow ? 'Tickets Bajos' : 'Tickets OK'}
              </p>
            </div>
              {isRecharging ? (
              <form onSubmit={handleRechargeSubmit} className="mt-3">
                <div className="flex flex-col space-y-3">
                  {/* Título de la sección */}
                  <div className={`p-3 rounded-md ${isPlusMode ? 'bg-purple-50' : 'bg-blue-50'}`}>
                    <h4 className={`text-sm font-semibold text-center ${isPlusMode ? 'text-purple-800' : 'text-blue-800'}`}>
                      {isPlusMode ? '🎫 RECARGAS PLUS - CANTIDAD Y TOKEN MANUALES' : '🎫 RECARGA POR PAQUETES DE TICKETS'}
                    </h4>
                    <p className={`text-xs text-center mt-1 ${isPlusMode ? 'text-purple-600' : 'text-blue-600'}`}>
                      {isPlusMode ? 'Ingresa la cantidad de tickets (el token se asigna automáticamente desde ticketRequestsInternas)' : 'Selecciona el tipo de vehículo y la cantidad de tickets'}
                    </p>
                  </div>

                  {/* Selector de tipo de vehículo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      1️⃣ Selecciona el tipo de vehículo:
                    </label>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedVehicleType('moto');
                          setSelectedPackage(null);
                          if (isPlusMode) {
                            setSelectedManualToken('');
                          }
                        }}
                        className={`flex-1 py-3 px-3 border rounded-md text-center font-semibold ${
                          selectedVehicleType === 'moto'
                            ? 'bg-blue-100 border-blue-500 text-blue-700 shadow-md'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        🏍️ MOTOS
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedVehicleType('auto');
                          setSelectedPackage(null);
                          if (isPlusMode) {
                            setSelectedManualToken('');
                          }
                        }}
                        className={`flex-1 py-3 px-3 border rounded-md text-center font-semibold ${
                          selectedVehicleType === 'auto'
                            ? 'bg-green-100 border-green-500 text-green-700 shadow-md'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        🚗 AUTOS
                      </button>
                    </div>
                  </div>

                  {/* Selector de método de pago */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      2️⃣ Selecciona el método de pago:
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('efectivo')}
                        className={`w-full py-3 px-2 border rounded-md text-center text-sm font-semibold ${
                          selectedPaymentMethod === 'efectivo'
                            ? 'bg-green-100 border-green-500 text-green-700 shadow-md'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        💵 EFECT.
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('transferencia')}
                        className={`w-full py-3 px-2 border rounded-md text-center text-sm font-semibold ${
                          selectedPaymentMethod === 'transferencia'
                            ? 'bg-blue-100 border-blue-500 text-blue-700 shadow-md'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        🏦 TRANSF.
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('deposito')}
                        className={`w-full py-3 px-2 border rounded-md text-center text-sm font-semibold ${
                          selectedPaymentMethod === 'deposito'
                            ? 'bg-indigo-100 border-indigo-500 text-indigo-700 shadow-md'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        🏛️ DEPÓS.
                      </button>
                    </div>
                  </div>

                  {/* Modo Plus: Inputs manuales para cantidad y token */}
                  {isPlusMode ? (
                    <>
                      {/* Input para cantidad de tickets manual */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          3️⃣ Ingresa la cantidad de tickets manualmente:
                        </label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={manualTicketAmount}
                          onChange={(e) => setManualTicketAmount(e.target.value)}
                          placeholder="Ej: 50, 100, 150..."
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Ingresa la cantidad exacta de tickets que deseas recargar
                        </p>
                      </div>

                      {/* Selector de token (solo Admin) */}
                      {isAdmin && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            4️⃣ Token (opcional, para referencia):
                          </label>
                          <select
                            value={selectedManualToken}
                            onChange={(e) => setSelectedManualToken(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          >
                            <option value="">-- Selecciona un token --</option>
                            {availableTokens.map((tokenData) => {
                              const tokenToShow = selectedVehicleType === 'moto' ? tokenData.tokenMoto : tokenData.tokenAuto;
                              const cantidadToShow = selectedVehicleType === 'moto' ? tokenData.cantidadMoto : tokenData.cantidadAuto;
                              if (!tokenToShow) return null;
                              return (
                                <option key={tokenData.id} value={tokenToShow}>
                                  {tokenToShow} - ({cantidadToShow} tickets disponibles)
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}

                      {/* Resumen de la recarga Plus */}
                      {manualTicketAmount && (
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border-2 border-purple-200">
                          <div className="text-center mb-3">
                            <h4 className="text-lg font-bold text-purple-800">
                              ✅ RECARGA PLUS CONFIGURADA
                            </h4>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center bg-white p-2 rounded">
                              <span className="text-sm font-medium text-gray-700">Tipo de vehículo:</span>
                              <span className="font-bold text-blue-600">
                                {selectedVehicleType === 'moto' ? '🏍️ MOTOS' : '🚗 AUTOS'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-2 rounded">
                              <span className="text-sm font-medium text-gray-700">Cantidad de tickets:</span>
                              <span className="font-bold text-purple-600">🎫 {manualTicketAmount}</span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-2 rounded">
                              <span className="text-sm font-medium text-gray-700">Token:</span>
                              <span className="font-bold text-indigo-600 text-xs">{selectedManualToken || 'Selección automática'}</span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-2 rounded">
                              <span className="text-sm font-medium text-gray-700">Precio aproximado:</span>
                              <span className="font-bold text-green-600">
                                💰 ${((parseInt(manualTicketAmount) || 0) * (selectedVehicleType === 'auto' ? 11.00 / 50 : 11.00 / 80)).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-2 rounded">
                              <span className="text-sm font-medium text-gray-700">Método de pago:</span>
                              <span className="font-bold text-orange-600">
                                {getPaymentMethodLabel(selectedPaymentMethod)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Modo normal: Selector de paquetes */
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        3️⃣ Selecciona un paquete para {selectedVehicleType.toUpperCase()}:
                      </label>
                      {process.env.NODE_ENV === 'development' && isAdmin && (availableTickets.tokenMoto || availableTickets.tokenAuto) && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                          🔑 Token que se enviará: <span className="font-mono font-semibold">{selectedVehicleType === 'moto' ? (availableTickets.tokenMoto || '—') : (availableTickets.tokenAuto || '—')}</span>
                        </p>
                      )}

                      <div className="grid grid-cols-1 gap-3">
                        {ticketPackages[selectedVehicleType].map((packageOption, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setSelectedPackage(packageOption)}
                            className={`py-4 px-4 border-2 rounded-lg text-left transition-all duration-200 ${
                              selectedPackage && selectedPackage.price === packageOption.price && selectedPackage.tickets === packageOption.tickets
                                ? 'bg-blue-100 border-blue-500 text-blue-700 shadow-lg transform scale-105'                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <div className="font-bold text-lg">
                                  🎫 {packageOption.tickets} tickets
                                </div>
                                <div className="text-sm font-medium text-green-600">
                                  💰 ${packageOption.price.toFixed(2)}
                                </div>
                              </div>
                              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {selectedVehicleType === 'moto' ? '🏍️ Motos' : '🚗 Autos'}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                    {/* Mostrar el paquete seleccionado (solo en modo normal) */}
                  {!isPlusMode && selectedPackage && (
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border-2 border-green-200">
                      <div className="text-center mb-3">
                        <h4 className="text-lg font-bold text-green-800">
                          ✅ PAQUETE SELECCIONADO
                        </h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center bg-white p-2 rounded">
                          <span className="text-sm font-medium text-gray-700">Tipo de vehículo:</span>
                          <span className="font-bold text-blue-600">
                            {selectedVehicleType === 'moto' ? '🏍️ MOTOS' : '🚗 AUTOS'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center bg-white p-2 rounded">
                          <span className="text-sm font-medium text-gray-700">Cantidad de tickets:</span>
                          <span className="font-bold text-purple-600">🎫 {selectedPackage.tickets}</span>
                        </div>
                        <div className="flex justify-between items-center bg-white p-2 rounded">
                          <span className="text-sm font-medium text-gray-700">Precio total:</span>
                          <span className="font-bold text-green-600">💰 ${selectedPackage.price.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center bg-white p-2 rounded">
                          <span className="text-sm font-medium text-gray-700">Método de pago:</span>
                          <span className="font-bold text-orange-600">
                            {getPaymentMethodLabel(selectedPaymentMethod)}
                          </span>
                        </div>
                        {process.env.NODE_ENV === 'development' && isAdmin && (availableTickets.tokenMoto || availableTickets.tokenAuto) && (
                          <div className="flex justify-between items-center bg-amber-50 p-2 rounded border border-amber-200">
                            <span className="text-xs font-medium text-amber-800">🔑 Token a la API:</span>
                            <span className="text-xs font-mono font-bold text-amber-900 truncate max-w-[180px]" title={selectedVehicleType === 'moto' ? availableTickets.tokenMoto : availableTickets.tokenAuto}>
                              {selectedVehicleType === 'moto' ? (availableTickets.tokenMoto || '—') : (availableTickets.tokenAuto || '—')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                    <div className="flex space-x-2 mt-4">
                    <button
                      type="submit"
                      disabled={!canRechargeEffective || (isPlusMode ? (!manualTicketAmount || !selectedManualToken) : !selectedPackage)}
                      className={`py-3 px-6 rounded-lg text-sm font-semibold flex-1 transition-all duration-200 ${
                        !canRechargeEffective
                          ? 'bg-red-300 text-red-600 cursor-not-allowed'
                          : (isPlusMode ? manualTicketAmount : selectedPackage)
                            ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg transform hover:scale-105'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      title={!canRechargeEffective ? (sinToken ? 'No hay token activo (en_uso=true) en ticketRequestsInternas' : `Sin tickets de ${selectedVehicleType === 'moto' ? 'motos' : 'autos'} en el sistema (${selectedVehicleType === 'moto' ? sistemaDisponibleMoto : sistemaDisponibleAuto})`) : ''}
                    >
                      {!canRechargeEffective
                        ? (sinToken ? '🚫 SIN TOKEN ACTIVO' : `🚫 SIN TICKETS DE ${selectedVehicleType === 'moto' ? 'MOTOS' : 'AUTOS'} (${selectedVehicleType === 'moto' ? sistemaDisponibleMoto : sistemaDisponibleAuto})`)
                        : isPlusMode
                          ? (manualTicketAmount ? '💳 CONFIRMAR RECARGA PLUS' : '❌ INGRESA CANTIDAD')
                          : (selectedPackage ? '💳 CONFIRMAR RECARGA' : '❌ SELECCIONE UN PAQUETE')
                      }
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsRecharging(false);
                        setSelectedPackage(null);
                        setSelectedPaymentMethod('efectivo');
                        setManualTicketAmount('');
                        setSelectedManualToken('');
                      }}
                      className="bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200"
                    >
                      ❌ CANCELAR
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              (isAdmin || isSupervisor) && (
                <div className="space-y-2">
                  <button
                    onClick={() => setIsRecharging(!isRecharging)}
                    className={`w-full flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                      ambosEnContra
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : isRecharging
                          ? 'bg-gray-500 hover:bg-gray-600 text-white'
                          : lowBalance && (isAdmin || isSupervisor)
                            ? 'bg-red-500 hover:bg-red-600 text-white'
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                    disabled={isRecharging || ambosEnContra}
                    title={ambosEnContra ? 'Sin tickets disponibles en el sistema' : ''}
                  >
                    {!canRecharge ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                        </svg>
                        🚫 Recarga Bloqueada
                      </>
                    ) : isRecharging ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        Recargar Tickets
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        Recargar Tickets
                      </>
                    )}
                  </button>
                  
                  {/* Mensaje explicativo cuando las recargas están bloqueadas */}
                  {!canRecharge && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-2">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <span className="text-red-400 text-sm">🚨</span>
                        </div>
                        <div className="ml-2">
                          <p className="text-xs text-red-700 font-medium">
                            Sistema sin tickets suficientes
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            Disponibles: {(availableTickets.cantidadMoto + availableTickets.cantidadAuto).toLocaleString()} 
                            <br />
                            Mínimo requerido: 1,000
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
          
          {/* Controles de administrador */}
          {isAdmin && (
            <div className="mt-3 pt-3 border-t flex justify-between">
              {isEditing ? (
                <form onSubmit={handleEditSubmit} className="w-full">
                  <div className="mb-2">
                    <label className="text-xs text-gray-500">Nombre</label>
                    <input 
                      type="text"
                      value={editData.nombre}
                      onChange={(e) => setEditData({...editData, nombre: e.target.value})}
                      className="w-full border rounded px-2 py-1 text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label className="text-xs text-gray-500">Cédula</label>
                    <input 
                      type="text"
                      value={editData.cedula}
                      onChange={(e) => setEditData({...editData, cedula: e.target.value})}
                      className="w-full border rounded px-2 py-1 text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label className="text-xs text-gray-500">Teléfono</label>
                    <input 
                      type="text"
                      value={editData.telefono}
                      onChange={(e) => setEditData({...editData, telefono: e.target.value})}
                      className="w-full border rounded px-2 py-1 text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label className="text-xs text-gray-500">Zona</label>
                    <input 
                      type="text"
                      value={editData.zona}
                      onChange={(e) => setEditData({...editData, zona: e.target.value})}
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div className="flex justify-between">
                    <button 
                      type="submit"
                      className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Guardar
                    </button>
                    <button 
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-1 rounded text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-1 rounded text-sm flex items-center"
                  >
                    Editar
                  </button>
                  <button
                    onClick={handleToggleStatus}
                    className={`${
                      vendor.status === 'active' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                    } text-white px-4 py-1 rounded text-sm flex items-center`}
                  >
                    {vendor.status === 'active' ? "Desactivar" : "Activar"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de confirmación */}
      <ModalDialog
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({...prev, isOpen: false}))}
        type={confirmModal.type}
      />
      
      {/* Modal de alerta */}      <ModalDialog
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={() => setAlertModal(prev => ({...prev, isOpen: false}))}
        type={alertModal.type}
      />
    </>
  );
});

export default VendorCard;
